import {
  AttachMoney,
  Check,
  Download,
  MoreHoriz,
  Undo,
} from "@mui/icons-material";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import React, { MouseEvent, useState } from "react";
import { useQueryClient } from "react-query";

import { usePushForm } from "contexts/FormDialogsContext";
import { getDealQueryKey, getDealsQueryKey } from "queries/useDeal";
import { getInvoicesQueryKey } from "queries/useInvoice";
import { InvoiceRead, InvoiceStatusEnum } from "types/api/deal/invoice";
import { coreService } from "utils/axios";
import { updateInvoiceAsync } from "utils/invoice";

interface InvoiceActionsMenuProps {
  invoice: InvoiceRead;
}

export const InvoiceActionsMenu: React.FC<InvoiceActionsMenuProps> = ({
  invoice,
}) => {
  const queryClient = useQueryClient();
  const pushForm = usePushForm();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      handleMenuClose();
      const response = await coreService.get(`invoice/${invoice.id}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.invoice_code}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the invoice:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsSent = async () => {
    handleMenuClose();
    await updateInvoiceAsync(invoice.id, { status: InvoiceStatusEnum.sent });
    await queryClient.invalidateQueries(getDealQueryKey(`${invoice.deal_id}`));
    await queryClient.invalidateQueries(getDealsQueryKey());
    await queryClient.invalidateQueries(getInvoicesQueryKey());
  };

  const handleMarkAsUnsent = async () => {
    handleMenuClose();
    await updateInvoiceAsync(invoice.id, { status: InvoiceStatusEnum.new });
    await queryClient.invalidateQueries(getDealQueryKey(`${invoice.deal_id}`));
    await queryClient.invalidateQueries(getDealsQueryKey());
    await queryClient.invalidateQueries(getInvoicesQueryKey());
  };

  const handleApplyPayment = () => {
    pushForm({
      identifier: "PaymentForm",
      changes: {
        invoice_id: invoice.id,
        invoice: { key: invoice.id, label: "" },
      },
    });
  };

  return (
    <>
      <IconButton onClick={handleMenuOpen} disabled={loading}>
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* Always show the Download option */}
        <MenuItem onClick={handleDownload} disabled={loading}>
          <ListItemIcon>
            <Download />
          </ListItemIcon>
          <ListItemText>{loading ? "Downloading..." : "Download"}</ListItemText>
        </MenuItem>

        {/* Conditionally show Mark as Sent */}
        {invoice.status === InvoiceStatusEnum.new && (
          <MenuItem onClick={handleMarkAsSent}>
            <ListItemIcon>
              <Check />
            </ListItemIcon>
            <ListItemText>Mark as Sent</ListItemText>
          </MenuItem>
        )}

        {/* Conditionally show Mark as Unsent */}
        {invoice.status === InvoiceStatusEnum.sent && (
          <MenuItem onClick={handleMarkAsUnsent}>
            <ListItemIcon>
              <Undo />
            </ListItemIcon>
            <ListItemText>Mark as Unsent</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleApplyPayment}>
          <ListItemIcon>
            <AttachMoney />
          </ListItemIcon>
          <ListItemText>Apply Payment</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
